import { Trash } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { ThinkerShareRole } from "~/api/aiAdminTypes";
import {
  useMembers,
  useRevokeThinkerShare,
  useShareThinker,
  useThinkerGroups,
  useThinkerShares,
  useWorkspaces,
} from "~/api/queries";
import { Pill, Row, Rows } from "~/features/automation/components";
import { Select } from "~/features/settings/controls";
import { Button } from "~/foundation/components/Button";
import { Dialog } from "~/foundation/components/Dialog";
import { IconButton } from "~/foundation/components/IconButton";
import { Input } from "~/foundation/components/Input";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { formatDateTime } from "~/foundation/utils/time";

type Recipient = "user" | "team" | "group";

/**
 * Share a thinker this workspace owns, and take a share back.
 *
 * Two roles, and the difference is not cosmetic: `use` makes the thinker runnable for the
 * recipient, `view` makes it appear in their picker greyed out and unrunnable. Both are listed
 * with their expiry, because a grant that quietly lapsed and a grant that was never made look
 * identical from the other side.
 */
export function ShareThinkerDialog({
  thinkerId,
  thinkerName,
  onClose,
}: {
  thinkerId: string;
  thinkerName: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { data: shares, isLoading } = useThinkerShares(thinkerId);
  const { data: members } = useMembers();
  const { data: groups } = useThinkerGroups();
  const { data: workspaces } = useWorkspaces();
  const share = useShareThinker();
  const revoke = useRevokeThinkerShare();

  const [recipientKind, setRecipientKind] = useState<Recipient>("user");
  const [recipientId, setRecipientId] = useState("");
  const [role, setRole] = useState<ThinkerShareRole>("use");
  const [expiresAt, setExpiresAt] = useState("");

  const alreadyShared = new Set(
    (shares ?? []).flatMap((grant) =>
      [grant.recipientUserId, grant.recipientTeamId, grant.recipientGroupId].filter(
        (value): value is string => Boolean(value),
      ),
    ),
  );

  const candidates =
    recipientKind === "user"
      ? (members ?? [])
          .filter((member) => !member.suspended && !alreadyShared.has(member.id))
          .map((member) => ({ id: member.id, name: member.name }))
      : recipientKind === "group"
        ? (groups ?? []).map((group) => ({ id: group.id, name: group.name }))
        : (workspaces ?? [])
            .filter((workspace) => !workspace.current)
            .map((workspace) => ({ id: workspace.id, name: workspace.name }));

  const submit = () => {
    if (!recipientId) {
      return;
    }
    share.mutate(
      {
        id: thinkerId,
        draft: {
          ...(recipientKind === "user" ? { recipientUserId: recipientId } : {}),
          ...(recipientKind === "team" ? { recipientTeamId: recipientId } : {}),
          ...(recipientKind === "group" ? { recipientGroupId: recipientId } : {}),
          role,
          ...(expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : {}),
        },
      },
      {
        onSuccess: () => {
          toast.success(t("Shared"));
          setRecipientId("");
          setExpiresAt("");
        },
      },
    );
  };

  const nameFor = (id: string | null, kind: Recipient): string => {
    if (!id) {
      return "";
    }
    if (kind === "user") {
      return (members ?? []).find((member) => member.id === id)?.name ?? t("Someone");
    }
    if (kind === "group") {
      return (groups ?? []).find((group) => group.id === id)?.name ?? t("A group");
    }
    return (
      (workspaces ?? []).find((workspace) => workspace.id === id)?.name ?? t("Another workspace")
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("Share {{name}}", { name: thinkerName })}
      closeLabel={t("Close")}
      size="lg"
      footer={
        <Button variant="ghost" onClick={onClose}>
          {t("Done")}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-2 sm:grid-cols-2">
          <Select
            label={t("Share with")}
            value={recipientKind}
            fullWidth
            onChange={(event) => {
              setRecipientKind(event.target.value as Recipient);
              setRecipientId("");
            }}
          >
            <option value="user">{t("A teammate")}</option>
            <option value="group">{t("A group")}</option>
            <option value="team">{t("Another workspace")}</option>
          </Select>
          <Select
            label={t("Recipient")}
            value={recipientId}
            fullWidth
            onChange={(event) => setRecipientId(event.target.value)}
          >
            <option value="">{t("Choose a recipient")}</option>
            {candidates.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.name}
              </option>
            ))}
          </Select>
          <Select
            label={t("Permission")}
            value={role}
            fullWidth
            hint={
              role === "view"
                ? t("Visible in their picker, but they cannot run it.")
                : t("They can run it in their own conversations.")
            }
            onChange={(event) => setRole(event.target.value as ThinkerShareRole)}
          >
            <option value="use">{t("Can use")}</option>
            <option value="view">{t("Can view")}</option>
          </Select>
          <Input
            label={t("Expires")}
            type="date"
            value={expiresAt}
            fullWidth
            hint={t("Optional. Blank means the grant does not lapse.")}
            onChange={(event) => setExpiresAt(event.target.value)}
          />
        </div>

        <div className="flex justify-end">
          <Button variant="primary" disabled={!recipientId || share.isPending} onClick={submit}>
            {t("Share")}
          </Button>
        </div>

        {isLoading ? (
          <Spinner label={t("Loading")} />
        ) : shares?.length ? (
          <Rows>
            {shares.map((grant) => {
              const kind: Recipient = grant.recipientUserId
                ? "user"
                : grant.recipientGroupId
                  ? "group"
                  : "team";
              const recipient =
                grant.recipientUserId ?? grant.recipientGroupId ?? grant.recipientTeamId;
              return (
                <Row key={grant.id}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-body text-text">{nameFor(recipient, kind)}</p>
                    <p className="truncate text-small text-text-tertiary">
                      {grant.expiresAt
                        ? t("Until {{date}}", {
                            date: formatDateTime(grant.expiresAt, i18n.language),
                          })
                        : t("No expiry")}
                    </p>
                  </div>
                  <Pill tone={grant.role === "view" ? "neutral" : "accent"}>
                    {grant.role === "view" ? t("Can view") : t("Can use")}
                  </Pill>
                  <IconButton
                    icon={Trash}
                    aria-label={t("Revoke")}
                    size="sm"
                    disabled={revoke.isPending}
                    onClick={() =>
                      revoke.mutate(grant.id, {
                        onSuccess: () => toast.success(t("Share revoked")),
                      })
                    }
                  />
                </Row>
              );
            })}
          </Rows>
        ) : (
          <Text variant="small">{t("Not shared with anyone yet.")}</Text>
        )}
      </div>
    </Dialog>
  );
}
